import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { LanguageService } from "../../core/services/language.service";
import { SeoService } from "../../core/services/seo.service";
import { SEO_PAGES } from "../../core/constants/seo-pages.constants";
import { APP_NAME, APP_CONSTANTS } from "../../core/constants/app.constants";

const LOCALES: Record<string, string> = {
  es: "Spanish",
  de: "German",
  fr: "French",
  it: "Italian",
  "pt-br": "Brazilian Portuguese",
  ja: "Japanese",
};
const SUPPORTED_LOCALES = Object.keys(LOCALES);

@Component({
  selector: "app-seo-page",
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./seo-page.component.html",
  styleUrl: "./seo-page.component.scss",
})
export class SeoPageComponent {
  readonly appName = APP_NAME;
  private readonly route = inject(ActivatedRoute);
  readonly copy = inject(LanguageService).copy;

  readonly locale = this.getLocale();
  readonly slug = this.getSlug();
  readonly page = SEO_PAGES[this.slug] ?? SEO_PAGES["home"];
  readonly localeLabel = LOCALES[this.locale];

  constructor() {
    const canonical = `${APP_CONSTANTS.siteUrl}/${this.route.snapshot.url.map((s) => s.path).join("/")}/`;
    inject(SeoService).updatePage({
      title: `${this.page.title} | ${APP_NAME}`,
      description: this.page.description,
      canonical,
      hreflangSlug: this.isLocaleHome() ? "" : this.slug,
    });
  }

  startChat() {
    // Navigating to /chat from a template routerLink is preferred; keep for backward compatibility
  }

  private getSlug(): string {
    const first = this.route.snapshot.paramMap.get("slug") ?? "home";
    const second = this.route.snapshot.paramMap.get("childSlug");
    return second ? `${first}/${second}` : first;
  }

  private isLocaleHome(): boolean {
    return this.route.snapshot.url.length === 1 && Boolean(this.locale);
  }

  private getLocale(): string {
    const routeLocale = this.route.snapshot.paramMap.get("locale");
    if (routeLocale) return routeLocale;
    const firstSegment = this.route.snapshot.url[0]?.path;
    return firstSegment && SUPPORTED_LOCALES.includes(firstSegment)
      ? firstSegment
      : "";
  }
}
